/**
 * SeriesPage — dedicated detail page for a series.
 *
 * Route: /series/:seriesKey
 *
 * What lives here (per user spec, May 15 2026):
 *   • Big hero cover + title / author / genre / description / difficulty.
 *   • "Continue Ep N" if there's a resume point, otherwise "Play first".
 *   • Episode list (drag-reorder for writer/admin; per-episode Edit + Delete).
 *   • Inline book-club thread scoped to this author's board so readers can
 *     discuss without leaving the page.
 *
 * Data: fetches `/api/library` once and filters by series_id to find the
 * matching series + its embedded episodes. (Same flow SeriesFolder uses,
 * which keeps caching/auth behaviour identical.)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Film, Headphones, Play, Edit, Trash2, Clock,
  GripVertical, Loader2, Plus, MessageCircle, MessageSquare, Heart, Send,
  CheckCircle2, Circle, PlayCircle, Sparkles, Star, BarChart3, Flag,
} from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { resolveCoverUrl } from '../utils/coverUrl';
import { canEditItem } from '../utils/canEdit';
import { Button } from '../components/ui/button';
import AddEpisodeModal from '../components/AddEpisodeModal';
import DownloadButton from '../components/DownloadButton';
import ReportContentModal from '../components/ReportContentModal';
import ItemComments from '../components/ItemComments';
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DIFFICULTY_TONE = {
  Beginner: 'border-emerald-400/50 text-emerald-200 bg-emerald-500/20',
  Intermediate: 'border-amber-400/50 text-amber-200 bg-amber-500/20',
  Advanced: 'border-rose-400/50 text-rose-200 bg-rose-500/20',
};

const fmtRel = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const SortableEpisodeRow = ({ id, children, dragEnabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id, disabled: !dragEnabled,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {children({ dragListeners: listeners })}
    </div>
  );
};

const SeriesPage = () => {
  const { seriesKey } = useParams();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  // June 2 2026 — per-episode progress + XP overlay state.
  // `progressMap[episode_id] = { completed, progress, time_listened_seconds, updated_at }`.
  const [progressMap, setProgressMap] = useState({});
  const [xpItems, setXpItems] = useState({});
  const [resumeId, setResumeId] = useState(null);
  // Per-episode comment thread expansion. Each episode has its OWN flat
  // comment thread (separate from the series-level Discussion Board below),
  // so we track which rows are currently expanded.
  const [openComments, setOpenComments] = useState(() => new Set());
  const toggleComments = (epId) => setOpenComments((prev) => {
    const next = new Set(prev);
    if (next.has(epId)) next.delete(epId); else next.add(epId);
    return next;
  });
  // DMCA-style "Report this series" modal.
  const [reportOpen, setReportOpen] = useState(false);

  const fetchSeries = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/library`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const found = (d.series || []).find(
        (s) => (s.series_id || s.book_id) === seriesKey,
      );
      setSeries(found || null);
    } catch (e) {
      toast.error(`Failed to load series: ${e?.message || 'unknown'}`);
    } finally {
      setLoading(false);
    }
  }, [seriesKey]);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/auth/me`);
        if (r.ok) setMe(await r.json());
      } catch { /* ignore */ }
    })();
    fetchSeries();
  }, [fetchSeries]);

  // Pull the listener's progress rows once we know the series. We
  // intersect by episode_id / chapter_id so an "already listened" mark
  // surfaces per row.
  useEffect(() => {
    if (!series) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/progress`);
        if (!r.ok) return;
        const body = await r.json();
        // Endpoint shape: { progress: [...] }. Tolerate older callers
        // that returned a bare array.
        const arr = Array.isArray(body) ? body : (body?.progress || []);
        const map = {};
        let mostRecent = null;
        (arr || []).forEach((p) => {
          const id = p.book_id;
          if (!id) return;
          map[id] = {
            completed: !!p.completed,
            progress: Number(p.progress || 0),
            time_listened_seconds: Number(p.time_listened_seconds || 0),
            updated_at: p.updated_at,
          };
          // Track the most-recent in-progress (not yet completed) episode
          // belonging to THIS series for auto-resume.
          const inThisSeries = (series.episodes || []).some(
            (e) => (e.episode_id || e.chapter_id || e.book_id) === id,
          );
          if (inThisSeries && !p.completed && p.updated_at) {
            const t = new Date(p.updated_at).getTime();
            if (!mostRecent || t > mostRecent.t) mostRecent = { id, t };
          }
        });
        if (cancelled) return;
        setProgressMap(map);
        setResumeId(mostRecent?.id || null);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [series]);

  // Owner-or-admin XP map.
  useEffect(() => {
    if (!me || (me.role !== 'writer' && me.role !== 'admin')) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/writer/content-xp`);
        if (r.ok && !cancelled) {
          const j = await r.json();
          setXpItems(j.items || {});
        }
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [me]);

  // Auto-scroll to the resume row when it materialises. Runs after
  // sortedEpisodes mounts; uses scrollIntoView with `center` block so
  // the resume row sits in the middle of the viewport. June 2 2026 user
  // spec — "the click should automatically pull the screen to the last
  // listened-to episode".
  useEffect(() => {
    if (!resumeId) return;
    // Defer by one frame so the row is in the DOM.
    const id = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="series-page-episode-${resumeId}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [resumeId]);

  const isOwnerOrAdmin = !!series && (
    me?.role === 'admin' ||
    (me?.role === 'writer' && me?.user_id && series?.user_id === me.user_id)
  );

  const isCinematic = series?.type === 'cinematic' || series?.content_type === 'cinematic';
  const Icon = isCinematic ? Film : Headphones;
  const canManage = !!series && canEditItem({
    userRole: me?.role,
    userId: me?.user_id,
    activeSpace: me?.active_space,
    item: series,
  });

  const sortedEpisodes = useMemo(() => (
    (series?.episodes || []).slice().sort(
      (a, b) => (a.episode_number || a.chapter_number || 0) - (b.episode_number || b.chapter_number || 0),
    )
  ), [series]);

  // Build a "padded" view where every position from 1 → max(episode_number)
  // gets a row. Real episodes keep their data; missing numbers become
  // placeholder rows so positions are physically reserved on screen even
  // when there's no upload yet. (User request — May 15 2026.)
  const maxNumber = sortedEpisodes.reduce(
    (acc, e) => Math.max(acc, e.episode_number || e.chapter_number || 0),
    0,
  );
  const epsByNumber = new Map();
  sortedEpisodes.forEach((e) => {
    const n = e.episode_number || e.chapter_number;
    if (n && !epsByNumber.has(n)) epsByNumber.set(n, e);
  });
  const paddedSlots = [];
  for (let n = 1; n <= maxNumber; n += 1) {
    paddedSlots.push({ number: n, episode: epsByNumber.get(n) || null });
  }
  // If no episode_numbers are set yet (legacy series), fall back to the
  // raw list so we still render something useful.
  const slots = paddedSlots.length > 0
    ? paddedSlots
    : sortedEpisodes.map((e, i) => ({ number: i + 1, episode: e }));

  /**
   * Strip an auto-generated "Episode N:" or "Episode N —" prefix from a
   * stored title when the N inside it duplicates the badge — this happens
   * after a Renumber 1→N because the upload modal auto-prefixed titles
   * with the original filename's number. Returns the original title
   * untouched if the prefix doesn't match or is absent.
   */
  const cleanTitle = (title, expectedNumber) => {
    if (!title) return '';
    const m = title.match(/^Episode\s+(\d+)\s*[:\-—]\s*(.+)$/i);
    if (!m) return title;
    // Always strip — even if numbers diverge — because the badge is the
    // source of truth and a duplicated prefix is visual noise.
    return m[2].trim() || title;
  };

  // ─── Episode reorder (writer/admin only) ──────────────────────────
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onEpisodeDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = sortedEpisodes.findIndex(
      (e) => (e.episode_id || e.chapter_id || e.book_id) === active.id,
    );
    const newIdx = sortedEpisodes.findIndex(
      (e) => (e.episode_id || e.chapter_id || e.book_id) === over.id,
    );
    if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return;
    const next = arrayMove(sortedEpisodes, oldIdx, newIdx);
    setSeries((s) => ({ ...s, episodes: next }));
    try { navigator.vibrate?.(12); } catch (_) { /* noop */ }
    const endpoint = isCinematic
      ? `${BACKEND_URL}/api/cinematic/series/${seriesKey}/episodes/reorder`
      : `${BACKEND_URL}/api/audiobook/series/${seriesKey}/chapters/reorder`;
    const tid = toast.loading('Saving new order…');
    try {
      const r = await authFetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: next.map((ep, index) => ({
            id: ep.episode_id || ep.chapter_id || ep.book_id,
            order: index,
          })),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('✓ Order saved', { id: tid });
      fetchSeries();
    } catch (e) {
      toast.error(`Failed to reorder: ${e?.message || 'unknown'}`, { id: tid });
    }
  };

  const handlePlay = (ep) => {
    const id = ep.episode_id || ep.chapter_id || ep.book_id;
    if (id) navigate(`/player/${id}`);
  };
  const handlePlayFirst = () => {
    if (sortedEpisodes.length === 0) return;
    handlePlay(sortedEpisodes[0]);
  };
  const handleDeleteEpisode = async (ep) => {
    if (!window.confirm(`Delete "${ep.title}"? This cannot be undone.`)) return;
    const epId = ep.episode_id || ep.chapter_id;
    const url = isCinematic
      ? `${BACKEND_URL}/api/cinematic/episode/${epId}`
      : `${BACKEND_URL}/api/audiobook/chapter/${epId}`;
    const tid = toast.loading('Deleting…');
    try {
      const r = await authFetch(url, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('✓ Deleted', { id: tid });
      fetchSeries();
    } catch (e) {
      toast.error(`Delete failed: ${e?.message || 'unknown'}`, { id: tid });
    }
  };

  // Iter 82 — Admin-only whole-series deletion. Wipes chapters, on-disk
  // files, `content` mirror rows, and the series doc itself. Server-side
  // guard (audiobook_routes.py:204) already restricts to owner-or-admin.
  const handleDeleteWholeSeries = async () => {
    const kind = isCinematic ? 'cinematic series' : 'audiobook series';
    const epCount = sortedEpisodes.length;
    const first = window.confirm(
      `Delete the ${kind} "${series.title}" and all ${epCount} episode${epCount === 1 ? '' : 's'}? It will be moved to Trash for 30 days and can be restored by an admin.`,
    );
    if (!first) return;
    const typed = window.prompt(
      `Type DELETE to confirm moving "${series.title}" to Trash. Admins have 30 days to restore it.`,
    );
    if (typed !== 'DELETE') {
      toast.info('Cancelled — series kept.');
      return;
    }
    const url = isCinematic
      ? `${BACKEND_URL}/api/cinematic/series/${seriesKey}`
      : `${BACKEND_URL}/api/audiobook/series/${seriesKey}`;
    const tid = toast.loading('Moving to Trash…');
    try {
      const r = await authFetch(url, { method: 'DELETE' });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      toast.success(isCinematic ? '✓ Series deleted' : '✓ Series moved to Trash (30-day recovery)', { id: tid });
      navigate('/library');
    } catch (e) {
      toast.error(`Delete failed: ${e?.message || 'unknown'}`, { id: tid });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="series-page-loading">
        <Loader2 className="w-6 h-6 text-indigo-300 animate-spin" />
      </div>
    );
  }
  if (!series) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center text-slate-300" data-testid="series-page-not-found">
        <p className="text-lg font-semibold mb-2">Series not found</p>
        <p className="text-sm text-slate-500 mb-4">It may have been removed or moved to the Vault.</p>
        <Button onClick={() => navigate('/library')} variant="outline">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back to Library
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-4 pb-24 pt-4" data-testid={`series-page-${seriesKey}`}>
      <button
        type="button"
        onClick={() => navigate('/library')}
        className="mb-4 inline-flex items-center gap-1 text-sm text-indigo-300 hover:text-indigo-200"
        data-testid="series-page-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back to Library
      </button>

      {/* Hero */}
      <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-5 mb-6">
        <div className="relative aspect-[2/3] sm:aspect-[3/4] rounded-xl overflow-hidden bg-slate-900 ring-1 ring-white/10 shadow-xl shadow-black/40 mx-auto sm:mx-0 w-40 sm:w-full">
          {series.cover_image ? (
            <img
              src={resolveCoverUrl(series.cover_image)}
              alt={series.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-500 bg-gradient-to-br from-indigo-700/40 to-fuchsia-700/30">
              <Icon className="w-12 h-12" />
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] uppercase tracking-widest font-bold text-indigo-300 inline-flex items-center gap-1">
              <Icon className="w-3 h-3" /> {isCinematic ? 'Cinematic series' : 'Audiobook series'}
            </span>
            {series.difficulty && DIFFICULTY_TONE[series.difficulty] && (
              <span
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${DIFFICULTY_TONE[series.difficulty]}`}
                data-testid={`series-page-difficulty-${seriesKey}`}
              >
                {series.difficulty}
              </span>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-white leading-tight">{series.title}</h1>
          <p className="text-sm text-gray-400 mt-1">
            by{' '}
            {series.user_id ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); navigate(`/writer/${series.user_id}`); }}
                className="text-indigo-200 font-semibold underline-offset-2 hover:underline focus:outline-none focus:underline"
                data-testid="series-author-link"
              >
                {series.author || 'Unknown'}
              </button>
            ) : (
              <span className="text-indigo-200 font-semibold">{series.author || 'Unknown'}</span>
            )}
          </p>
          {series.genre && (
            <span className="inline-block mt-2 text-xs px-2 py-1 bg-slate-700 rounded text-slate-200">{series.genre}</span>
          )}
          {series.description && (
            <p className="text-sm text-gray-300 leading-relaxed mt-4 whitespace-pre-line">
              {series.description}
            </p>
          )}
          <div className="mt-5 flex flex-wrap gap-2">
            {sortedEpisodes.length > 0 && (
              <Button
                onClick={handlePlayFirst}
                className="bg-gradient-to-r from-fuchsia-500 to-indigo-600 hover:from-fuchsia-400 hover:to-indigo-500 text-white font-bold"
                data-testid="series-page-play-first"
              >
                <Play className="w-4 h-4 mr-2" />
                Play first episode
              </Button>
            )}
            <span className="inline-flex items-center px-3 py-1.5 rounded-md bg-slate-800/60 border border-slate-700 text-xs text-gray-300">
              {sortedEpisodes.length} {sortedEpisodes.length === 1 ? 'episode' : 'episodes'}
            </span>
            {canManage && (
              <button
                type="button"
                onClick={() => navigate(`/improve?ref_id=${encodeURIComponent(seriesKey)}`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gradient-to-r from-fuchsia-600/20 to-indigo-600/20 hover:from-fuchsia-600/40 hover:to-indigo-600/40 border border-indigo-400/40 text-xs text-indigo-200 font-bold transition"
                data-testid="series-page-open-improve"
                title="Open Improve analytics"
              >
                <BarChart3 className="w-3.5 h-3.5" />
                Improve
              </button>
            )}
            {!canManage && (
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-800/60 hover:bg-rose-600/20 border border-slate-700 hover:border-rose-400/40 text-xs text-slate-300 hover:text-rose-200 font-semibold transition"
                data-testid="series-page-report"
                title="Report this series"
              >
                <Flag className="w-3.5 h-3.5" />
                Report
              </button>
            )}
            {(series.play_count || 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-slate-800/60 border border-slate-700 text-xs text-gray-300"
                data-testid="series-play-count"
                title="Total plays across this series"
              >
                <Play className="w-3 h-3 text-emerald-400" />
                <strong className="text-white tabular-nums">
                  {(series.play_count || 0).toLocaleString()}
                </strong>
                {series.play_count === 1 ? 'play' : 'plays'}
              </span>
            )}
            {series.rating_avg != null && (
              <span
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-amber-500/10 border border-amber-400/40 text-xs text-amber-200"
                data-testid="series-rating-avg"
                title={`${series.rating_count || 0} rating${series.rating_count === 1 ? '' : 's'}`}
              >
                <Star className="w-3 h-3 fill-amber-300 text-amber-300" />
                <strong className="text-white tabular-nums">
                  {Number(series.rating_avg).toFixed(1)}
                </strong>
                <span className="text-amber-300/70">/ 5</span>
                {(series.rating_count || 0) > 0 && (
                  <span className="text-amber-300/60 ml-1 tabular-nums">
                    ({(series.rating_count || 0).toLocaleString()})
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Episode list */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-white">Episodes</h2>
          <div className="flex items-center gap-2">
            {me?.role === 'admin' && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleDeleteWholeSeries}
                className="border-red-500/50 bg-red-500/10 hover:bg-red-500/20 text-red-200 hover:text-red-100"
                data-testid="series-page-delete-whole-series"
                title="Delete the entire series (admin only)"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete series
              </Button>
            )}
            {canManage && (
              <Button
                size="sm"
                onClick={() => setAddOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-500 text-white"
                data-testid="series-page-add-episode"
              >
                <Plus className="w-4 h-4 mr-1" /> Add {isCinematic ? 'episode' : 'chapter'}
              </Button>
            )}
          </div>
        </div>

        {slots.length === 0 ? (
          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-6 text-center text-slate-500 text-sm">
            No episodes uploaded yet
          </div>
        ) : (
          <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={onEpisodeDragEnd}>
            <SortableContext
              items={sortedEpisodes.map((e) => e.episode_id || e.chapter_id || e.book_id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y divide-slate-800 rounded-lg border border-slate-800 overflow-hidden">
                {slots.map(({ number, episode: ep }) => {
                  // Empty slot — placeholder row to reserve the position.
                  if (!ep) {
                    return (
                      <li
                        key={`empty-${number}`}
                        className="flex items-center gap-3 p-3 bg-slate-900/30 border-l-4 border-dashed border-slate-700/60"
                        data-testid={`series-page-slot-empty-${number}`}
                      >
                        <div className="w-7 h-9 shrink-0" aria-hidden="true" />
                        <div className="flex items-center justify-center w-9 h-9 bg-slate-800/60 border border-dashed border-slate-700 rounded-md text-slate-500 font-bold text-sm shrink-0">
                          {number}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-400 font-medium italic">Episode {number} — empty slot</p>
                          <p className="text-[11px] text-slate-600 mt-0.5">Position reserved · upload to fill</p>
                        </div>
                        {canManage && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-indigo-300 border-indigo-500/40 hover:bg-indigo-500/10 h-8"
                            onClick={() => setAddOpen(true)}
                            data-testid={`series-page-slot-upload-${number}`}
                          >
                            <Plus className="w-3.5 h-3.5 mr-1" /> Upload
                          </Button>
                        )}
                      </li>
                    );
                  }

                  const epId = ep.episode_id || ep.chapter_id || ep.book_id;
                  const epNum = ep.episode_number || ep.chapter_number || number;
                  const displayTitle = cleanTitle(ep.title, epNum)
                    || `Episode ${epNum}`;
                  const prog = progressMap[epId];
                  const isResume = epId === resumeId;
                  const isCompleted = !!prog?.completed;
                  const isInProgress = !!prog && !prog.completed && (prog.progress > 0 || prog.time_listened_seconds > 0);
                  const ownerXP = xpItems[epId];
                  return (
                    <SortableEpisodeRow key={epId} id={epId} dragEnabled={canManage}>
                      {({ dragListeners }) => (
                        <>
                        <li
                          role="button"
                          tabIndex={0}
                          onClick={() => handlePlay(ep)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePlay(ep); }
                          }}
                          className={`flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 p-3 hover:bg-slate-800/70 active:bg-slate-800 cursor-pointer focus:outline-none focus:ring-2 ${
                            isResume
                              ? 'bg-amber-500/15 border-l-4 border-amber-400/80 focus:ring-amber-400/50'
                              : isCompleted
                              ? 'bg-emerald-950/40 focus:ring-emerald-500/40'
                              : 'bg-slate-900/60 focus:ring-indigo-500/40'
                          }`}
                          data-testid={`series-page-episode-${epId}`}
                        >
                          {canManage && (
                            <button
                              type="button"
                              {...(dragListeners || {})}
                              onClick={(e) => e.stopPropagation()}
                              className="touch-none select-none cursor-grab active:cursor-grabbing text-gray-500 hover:text-indigo-300 shrink-0 w-7 h-9 flex items-center justify-center"
                              aria-label={`Drag to reorder ${displayTitle}`}
                              title="Drag to reorder"
                              data-testid={`series-page-drag-${epId}`}
                            >
                              <GripVertical className="w-5 h-5" />
                            </button>
                          )}
                          {/* Listened-state indicator (June 2 2026). */}
                          <div
                            className="w-4 h-4 shrink-0 flex items-center justify-center"
                            data-testid={`series-page-episode-state-${epId}`}
                            title={
                              isCompleted ? 'Completed'
                              : isResume ? 'Pick up where you left off'
                              : isInProgress ? 'In progress'
                              : 'Not started'
                            }
                          >
                            {isCompleted ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            ) : isResume ? (
                              <PlayCircle className="w-4 h-4 text-amber-300 animate-pulse" />
                            ) : isInProgress ? (
                              <PlayCircle className="w-4 h-4 text-indigo-300" />
                            ) : (
                              <Circle className="w-3.5 h-3.5 text-slate-600" />
                            )}
                          </div>
                          <div className="flex items-center justify-center w-9 h-9 bg-indigo-600/25 rounded-md text-indigo-300 font-bold text-sm shrink-0">
                            {epNum}
                          </div>
                          <div className="flex-1 min-w-0 basis-[55%] sm:basis-auto">
                            <p className="text-sm text-white font-medium truncate flex items-center gap-2">
                              {displayTitle}
                              {isResume && (
                                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-200 bg-amber-500/20 border border-amber-400/40 rounded-full px-1.5 py-0.5">
                                  Resume
                                </span>
                              )}
                            </p>
                            <p className="text-[11px] text-indigo-200/80 inline-flex items-center gap-2 mt-0.5 overflow-hidden max-w-full">
                              {(ep.duration || ep.duration_seconds) && (
                                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                                  <Clock className="w-3 h-3" />
                                  {ep.duration || `${Math.round((ep.duration_seconds || 0) / 60)} min`}
                                </span>
                              )}
                              {isOwnerOrAdmin && ep.word_count > 0 && (
                                <span
                                  className="px-1.5 py-0.5 rounded-full border border-slate-500/40 bg-slate-700/50 text-slate-200 font-bold uppercase tracking-wider text-[10px] whitespace-nowrap"
                                  data-testid={`series-page-episode-words-${epId}`}
                                  title="Visible to writer & admins only"
                                >
                                  {ep.word_count.toLocaleString()}w
                                </span>
                              )}
                              {isOwnerOrAdmin && ownerXP != null && ownerXP > 0 && (
                                <span
                                  className="px-1.5 py-0.5 rounded-full border border-amber-400/40 bg-amber-500/15 text-amber-200 font-bold uppercase tracking-wider text-[10px] inline-flex items-center gap-1 whitespace-nowrap"
                                  data-testid={`series-page-episode-xp-${epId}`}
                                  title="XP earned — visible to writer & admins only"
                                >
                                  <Sparkles className="w-3 h-3" /> {ownerXP.toLocaleString()} XP
                                </span>
                              )}
                            </p>
                            {isOwnerOrAdmin && (
                              <p
                                className="text-[10.5px] text-slate-400 mt-1 inline-flex items-center gap-2 flex-wrap"
                                data-testid={`series-page-episode-retention-${epId}`}
                              >
                                <span className="tabular-nums">
                                  {(ep.play_count || ep.plays || 0).toLocaleString()} plays
                                </span>
                                <span className="text-slate-600">·</span>
                                <span>
                                  {ep.created_at
                                    ? new Date(ep.created_at).toLocaleDateString(undefined, {
                                        day: 'numeric', month: 'short', year: 'numeric',
                                      })
                                    : '—'}
                                </span>
                                <span className="text-slate-600">·</span>
                                <span className="italic text-slate-500">Retention is being calculated</span>
                              </p>
                            )}
                          </div>
                          <div
                            className="flex items-center gap-1 ml-auto shrink-0 order-3 sm:order-none basis-full sm:basis-auto justify-end pt-1 sm:pt-0 border-t sm:border-0 border-slate-800/60"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {(ep.file_url || ep.audio_url) && (
                              <DownloadButton
                                id={epId}
                                url={ep.file_url || ep.audio_url}
                                title={displayTitle}
                                meta={{
                                  cover_image: ep.cover_image || series.cover_image,
                                  series_id: seriesKey,
                                  series_title: series.title,
                                  episode_number: epNum,
                                  duration_seconds: ep.duration_seconds,
                                  type: isCinematic ? 'cinematic' : 'audiobook',
                                }}
                                size="compact"
                                iconOnlyOnMobile
                              />
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-8 w-8 p-0 ${openComments.has(epId) ? 'text-indigo-300' : 'text-gray-400 hover:text-white'}`}
                              onClick={(e) => { e.stopPropagation(); toggleComments(epId); }}
                              title="Comments on this episode"
                              aria-expanded={openComments.has(epId)}
                              data-testid={`series-page-episode-comments-toggle-${epId}`}
                            >
                              <MessageSquare className="w-4 h-4" />
                            </Button>
                            {canManage && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-gray-400 hover:text-white h-8 w-8 p-0"
                                  onClick={(e) => { e.stopPropagation(); }}
                                  title="Rename in Manage Content"
                                  data-testid={`series-page-edit-${epId}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteEpisode(ep); }}
                                  title="Delete episode"
                                  data-testid={`series-page-delete-${epId}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </li>
                        {openComments.has(epId) && (
                          <li
                            className="bg-slate-900/80 px-3 sm:px-5 py-3 border-l-4 border-indigo-500/40"
                            data-testid={`series-page-episode-comments-${epId}`}
                          >
                            <ItemComments refId={epId} refType="episode" />
                          </li>
                        )}
                        </>
                      )}
                    </SortableEpisodeRow>
                  );
                })}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </section>

      {/* Series-level Discussion Board — separate from the per-episode
          comment threads above. This is the author's community board where
          readers chat about the whole show. Episode-specific feedback lives
          on each row's MessageSquare toggle. */}
      <SeriesDiscussion series={series} me={me} />

      {addOpen && (
        <AddEpisodeModal
          series={series}
          existingEpisodes={sortedEpisodes}
          onClose={() => setAddOpen(false)}
          onUploaded={() => { setAddOpen(false); fetchSeries(); }}
        />
      )}

      <ReportContentModal
        refId={seriesKey}
        refTitle={series?.title}
        open={reportOpen}
        onClose={() => setReportOpen(false)}
      />
    </div>
  );
};

/**
 * SeriesDiscussion — compact inline book-club thread scoped to the
 * author's board (existing /book-club/posts?board=author:<slug>). Lets
 * readers post + like + open the full thread without leaving the page.
 */
const SeriesDiscussion = ({ series, me }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const slug = (series?.author || '').trim().toLowerCase().replace(/\s+/g, '_').slice(0, 48);
  const board = slug ? `author:${slug}` : 'general';
  const boardLabel = series?.author || 'General';

  const load = useCallback(async () => {
    if (!board) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/posts?board=${encodeURIComponent(board)}`);
      if (r.ok) {
        const d = await r.json();
        setPosts((d.posts || []).slice(0, 5));
      }
    } catch { /* ignore */ }
  }, [board]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ board, body: body.trim(), title: '' }),
      });
      if (!r.ok) throw new Error(await r.text());
      setBody('');
      load();
      toast.success('Posted to discussion');
    } catch (err) {
      toast.error(`Failed: ${err?.message || err}`);
    }
    setBusy(false);
  };

  const toggleLike = async (post) => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/book-club/posts/${post.post_id}/like`, { method: 'POST' });
      if (r.ok) {
        const d = await r.json();
        setPosts((prev) => prev.map((p) => p.post_id === post.post_id ? { ...p, like_count: d.like_count } : p));
      }
    } catch { /* ignore */ }
  };

  return (
    <section className="mt-2" data-testid="series-page-discussion">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-bold text-white inline-flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-indigo-300" />
          Discussion Board
          <span className="text-xs text-gray-500 font-normal">· {boardLabel}</span>
        </h2>
        <button
          type="button"
          onClick={() => navigate('/book-club')}
          className="text-xs text-indigo-300 hover:text-indigo-200"
          data-testid="series-page-discussion-open"
        >
          View all →
        </button>
      </div>
      <p className="text-[11px] text-slate-500 -mt-2 mb-3">
        Series-wide thread. For feedback on a specific episode, use the
        <MessageSquare className="inline w-3 h-3 mx-1 -translate-y-0.5" />
        icon on that episode row.
      </p>

      {me && (
        <form
          onSubmit={submit}
          className="mb-3 flex items-end gap-2 bg-slate-900/50 border border-slate-700 rounded-xl p-2"
          data-testid="series-page-discussion-compose"
        >
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={2}
            maxLength={4000}
            placeholder={`Share thoughts about ${series?.title || 'this series'}…`}
            className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:border-indigo-400 focus:outline-none resize-none"
            data-testid="series-page-discussion-body"
          />
          <button
            type="submit"
            disabled={busy || !body.trim()}
            className="h-10 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="series-page-discussion-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Post
          </button>
        </form>
      )}

      {posts.length === 0 ? (
        <p className="text-sm text-slate-500 px-1">No discussion yet. Be the first.</p>
      ) : (
        <ul className="space-y-2">
          {posts.map((p) => (
            <li
              key={p.post_id}
              className="bg-slate-900/40 border border-slate-700 rounded-xl p-3"
              data-testid={`series-page-discussion-post-${p.post_id}`}
            >
              <p className="text-[11px] text-gray-400">
                <span className="font-semibold text-indigo-200">{p.author_name}</span>
                {' · '}{fmtRel(p.created_at)}
              </p>
              <p className="text-sm text-gray-100 mt-1 whitespace-pre-line line-clamp-4">{p.body}</p>
              <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-400">
                <span className="inline-flex items-center gap-1">
                  <MessageCircle className="w-3.5 h-3.5" /> {p.reply_count || 0}
                </span>
                <button
                  type="button"
                  onClick={() => toggleLike(p)}
                  className="inline-flex items-center gap-1 hover:text-pink-300"
                  data-testid={`series-page-discussion-like-${p.post_id}`}
                >
                  <Heart className="w-3.5 h-3.5" /> {p.like_count || 0}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default SeriesPage;
